"use client";

import React from "react";
import { Facebook, Instagram, Twitter, MapPin, Phone, Mail, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function FooterMenuConsumer({
  title
}: {
  title: string;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        <h3 className="font-default text-lg font-semibold text-foreground">{title}</h3>
        <div className="flex flex-col gap-2">
          {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-24 bg-muted animate-pulse rounded-md" />)}
        </div>
      </div>;
  }
  return <div className="flex flex-col gap-4">
      <h3 className="font-default text-lg font-semibold text-foreground">{title}</h3>
      <NavigationMenu viewport={false} className="max-w-full justify-start">
        <NavigationMenuList className="flex-col items-start gap-2 space-x-0">
          {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i} className="w-full">
              <NavigationMenuLink asChild className="w-full justify-start px-0 py-1 h-auto text-muted-foreground hover:text-primary hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent data-[active=true]:text-primary transition-colors">
                <Link to={item.href || "#"}>{item.label}</Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>
    </div>;
}
export default function Footer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  };
  const socialLinks = [{
    icon: Instagram,
    href: "#",
    label: "Instagram"
  }, {
    icon: Twitter,
    href: "#",
    label: "X"
  }, {
    icon: Facebook,
    href: "#",
    label: "LinkedIn"
  }];
  return <section data-section-id="264869" id="footer" className="bg-background relative pt-16 pb-8 text-foreground">
      <Separator className="absolute top-0 left-0 w-full bg-border" />
      
      <div ref={ref} className={`container mx-auto px-4 md:px-6 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-12">
          
          {}
          <div className="flex flex-col items-start gap-4">
            <Link to="/" className="inline-block">
              <WvcLogo className="h-8 w-auto text-primary" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs font-sans">
              Freelance composer and music producer crafting cinematic soundscapes and rhythmic narratives for film, games, and digital media.
            </p>
          </div>

          {}
          <div>
            <MenuProvider menu_id="footer_menu">
              <FooterMenuConsumer title="" />
            </MenuProvider>
          </div>

          {}
          <div>
            <MenuProvider menu_id="46">
              <FooterMenuConsumer title="Navigation" />
            </MenuProvider>
          </div>

          {}
          <div className="flex flex-col gap-6">
            <div>
              
              <ul className="flex flex-col gap-3 text-sm text-muted-foreground font-mono">
                <li className="flex items-start gap-3">
                  <MapPin className="size-4 mt-0.5 shrink-0 text-secondary" />
                  <span>Dublin / Remote Worldwide</span>
                </li>
                
                
              </ul>
            </div>
            
            <div className="flex items-center gap-3">
              {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <Button key={i} data-index={i} variant="outline" size="icon" className="rounded-md size-9 text-muted-foreground border-border hover:text-primary hover:border-primary transition-colors bg-transparent" asChild>
                    <Link to={social.href} aria-label={social.label}>
                      <Icon className="size-4 stroke-[1px]" />
                    </Link>
                  </Button>;
            })}
            </div>
          </div>
        </div>

        <Separator className="mb-8 bg-border" />

        {}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-mono text-xs text-muted-foreground text-center md:text-left uppercase tracking-widest">© 2026 JOHANNES NORRIS-BROWN. All rights reserved.</p>
          
          <Button variant="secondary" size="sm" onClick={scrollToTop} className="gap-2 rounded-md font-mono text-xs uppercase tracking-wider">
            Back to Top
            <ArrowUp className="size-3" />
          </Button>
        </div>
      </div>
    </section>;
}